/**
 * The practice session's client-side state (brief §practice: "state in
 * memory + sessionStorage so refresh survives"). Pure data helpers — the UI
 * lives in `SessionPage.tsx`.
 */
import { belongsToOwner, getStorageOwner } from "@client/lib/storageOwner";
import type { ConfidenceLevel } from "@shared/attemptAnswer";
import type { Localized, PublicQuestion } from "@shared/types";
import type {
  AnswerOrigin,
  AnswerPartFeedback,
  ContrastOffer,
  FollowUpKind,
  NearMissKind,
  PendingPartFeedback,
  PlanOffer,
  PracticeMode,
  PredictionResult,
  LectureIdentity,
  RevealedQuestion,
} from "./types";

/** The server's `answer.open.*` cap (`z.string().max(4000)`, a UTF-16 length). */
export const OPEN_TEXT_MAX = 4000;

const STORAGE_KEY = "mf.practice.session";

/** A graded part row; `typed` is what the student wrote, kept when the grader did not echo a value. */
export interface ResultPart extends AnswerPartFeedback {
  typed?: string;
}

/** One checked part of a segmented item (pedagogy spec §4.3.4): locked, never re-checked. */
export interface PartCheck {
  /** The part's first-try value as typed; the final `/answer` sends it. */
  value: string;
  /** The rating given just before this check (§13 Q5: confidence per part). */
  confidence: ConfidenceLevel;
  outcome: "right" | "wrong";
  nearMissKind?: NearMissKind;
}

/**
 * The first try of a withheld item (pedagogy spec §5.3.6), kept while the retry is pending and
 * after it. `QuestionResult.correct`, `.score` and `.maxScore` stay the first try's values, so
 * the session score counts first tries (§13 Q12); what the retry did is in `retried`.
 */
export interface FirstTry {
  attemptId: string;
  correct: boolean;
  score: number;
  maxScore: number;
  /** Which parts were right (kept on the retry), with the near-miss kinds and diagnoses. */
  parts?: PendingPartFeedback[];
  /** What was typed for every part: resent for the kept parts, echoed in the diagnosis row. */
  numericRaw?: Record<string, string>;
  /** The option the student picked first (single-select MC). */
  mcSelected?: string[];
  /** The authored reason the picked option is wrong, when there is one. */
  mcDiagnosis?: { optionId: string; message: Localized };
  /** The pending response opened the next hint by itself (it is the last of `hintsRevealed`). */
  autoHint?: boolean;
}

/**
 * Why a question sits in the session although the batch did not hold it (step 2.6): the answered
 * question it follows (`of`) and, for a contrast twin, what it changes and the prediction made
 * before it was inserted. The first try sends `of`, `kind` and the choice with the answer.
 */
export interface FollowUpState {
  of: string;
  kind: FollowUpKind;
  /** Contrast: what differs from the question it follows ("φ = 90° → 180°"). */
  varies?: Localized;
  /**
   * Contrast with an authored prediction: its prompt, the options of an option-form prediction (a
   * verb form has none) and the choice, a verb or an option id. Required before insertion.
   */
  prediction?: {
    prompt: Localized;
    options?: { id: string; text: Localized }[];
    choice: string;
  };
}

export interface QuestionResult {
  /** A graded `/answer` (or self-graded open) attempt has been recorded. */
  answered: boolean;
  /** Nothing more to do on this question — answered, or given up on. */
  terminal: boolean;
  correct?: boolean;
  score?: number;
  maxScore?: number;
  parts?: ResultPart[];
  ticked?: string[];
  revealed?: RevealedQuestion;
  gaveUp?: boolean;
  /** Hint texts already fetched, in order (index i = hint i). */
  hintsRevealed: Localized[];
  /** "Klar" was pressed on an open question: solution shown, not yet self-graded. */
  openRevealPending: boolean;
  /**
   * The open answer as it stood when "Klar" was pressed. It is read-only from then on and is
   * the `open.text` the save sends. Absent in sessions saved before this field existed.
   */
  preRevealText?: string;
  /** The MC justification that was sent with the answer, echoed in the feedback. */
  justification?: string;
  /**
   * The MC option id(s) the student picked, echoed in `FeedbackPanel` as a
   * "Ditt val" badge (UX-04). Absent in sessions saved before this field
   * existed, and for non-MC questions.
   */
  selectedOptionIds?: string[];
  /** "Öva på en liknande uppgift" was used: a problem was inserted next, or none was left. */
  relatedStatus?: "added" | "none";
  /** "Hur säker är du?" as picked before the check (sent with the answer; required). */
  confidence?: ConfidenceLevel;
  /** The stored attempt's id from `/answer` (the guided retry of step 2.4 names it). */
  attemptId?: string;
  /** The plan-first pick, an anchor topic id (pedagogy spec §4.3.3); final once set. */
  planPick?: string;
  /** The first try was withheld: the retry is pending (answered, not terminal; no `revealed`). */
  retryPending?: boolean;
  /** The first try, kept while the retry is pending and after it. */
  firstTry?: FirstTry;
  /** The second try's outcome; absent when the retry was skipped ("Jag kör fast"). */
  retried?: { correct: boolean; score: number; maxScore: number };
  /** The solution is open from the start: the retry was skipped and `/reveal` answered. */
  solutionOpen?: boolean;
  /** A wrong single-select pick's authored diagnosis (terminal; the pending one is in `firstTry`). */
  mcDiagnosis?: { optionId: string; message: Localized };
  /** `YYYY-MM-DD`: the day Today re-tests this key (shown from step 2.7, kept until then). */
  retestDue?: string;
  /** Segmented items (pedagogy spec §4.3.4): the parts checked so far, by part id. */
  partChecks?: Record<string, PartCheck>;
  /**
   * The contrast twins the terminal answer offered (step 2.6). Absent on a follow-up (depth 1),
   * on a withheld try and after "Jag kör fast", which `/reveal` answers without them.
   */
  followUps?: ContrastOffer[];
  /** Set when this question was inserted as a follow-up; its first try sends it. */
  followUp?: FollowUpState;
  /** A contrast follow-up's terminal verdict on the prediction made before it. */
  predictionResult?: PredictionResult;
}

export interface PracticeSessionData {
  /**
   * This session's identity, a nonce made when it is created. A late answer patches the stored
   * session only if the ids match (`leaveSubmit.ts#patchStoredResult`), so it can never land on a
   * newer session that holds the same question. Absent in sessions saved before this field existed.
   */
  id?: string;
  lecture?: LectureIdentity;
  startedAt: number;
  mode: PracticeMode;
  questions: PublicQuestion[];
  index: number;
  results: Record<string, QuestionResult>;
  /** Plan-first offers by question id (mixed batches only; absent in older sessions). */
  plans?: Record<string, PlanOffer>;
  /**
   * Today's hand-off (step 2.7b): the `origin` each question's first try sends, by question id.
   * Only Today's own items have one; an inserted follow-up does not.
   */
  origins?: Record<string, AnswerOrigin>;
  /** Where the summary's "Fortsätt med dagens pass" goes (`/practice/today?done=…`). */
  returnTo?: string;
  /** The exam-wrapper set this session is (step 2.8c); absent for ordinary practice. */
  set?: SessionSet;
  /** The timed pair's clock: shown in the header, never submitted for the student (§7.3.6). */
  timed?: TimedClock;
}

/**
 * The set a session belongs to: an exam-wrapper set (`from` is the exam session it was suggested
 * from) or Tentaläge's timed drill (slice D, P2-3).
 */
export type SessionSet = { kind: "timed-pair"; from?: string } | { kind: "timed-drill" };

/**
 * The timed pair's countdown: `limitSec` from `startedAt` (ms). Elapsed time is not stored on any
 * answer (spec §13 Q28); a refresh keeps the clock because it runs from the stored start.
 */
export interface TimedClock {
  limitSec: number;
  startedAt: number;
  /** How many problems the clock is for; absent means the timed pair's two. */
  problems?: number;
}

/**
 * What a Today step or an exam-wrapper set adds to a session: the origin of each item, where the
 * summary leads, and the set's own data.
 */
export interface SessionExtras {
  origins?: Record<string, AnswerOrigin>;
  returnTo?: string;
  set?: SessionSet;
  timed?: TimedClock;
}

/** A new session's identity: unique per session, not a secret. */
function newSessionId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* an insecure context has no randomUUID: fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Dispatched on `window` after a result was patched into the stored session by a page that is
 * gone (a late answer). A mounted `SessionPage` listens and takes that result over, since its own
 * copy was loaded before the answer came back and its next save would overwrite the patch.
 */
export const SESSION_PATCHED_EVENT = "mf:session-patched";

/** The `detail` of `SESSION_PATCHED_EVENT`; `sessionId` is absent for a session without an id. */
export interface SessionPatchedDetail {
  sessionId?: string;
  questionId: string;
}

export function emptyResult(): QuestionResult {
  return { answered: false, terminal: false, hintsRevealed: [], openRevealPending: false };
}

export function createSession(
  mode: PracticeMode,
  questions: PublicQuestion[],
  plans?: Record<string, PlanOffer>,
  extras: SessionExtras = {},
): PracticeSessionData {
  const results: Record<string, QuestionResult> = {};
  for (const question of questions) results[question.id] = emptyResult();
  // Only offers for this batch's questions: a question inserted later (a follow-up) has none.
  const kept = Object.fromEntries(Object.entries(plans ?? {}).filter(([id]) => id in results));
  const origins = Object.fromEntries(
    Object.entries(extras.origins ?? {}).filter(([id]) => id in results),
  );
  return {
    id: newSessionId(),
    startedAt: Date.now(),
    mode,
    questions,
    index: 0,
    results,
    ...(Object.keys(kept).length > 0 ? { plans: kept } : {}),
    ...(Object.keys(origins).length > 0 ? { origins } : {}),
    ...(extras.returnTo ? { returnTo: extras.returnTo } : {}),
    ...(extras.set ? { set: extras.set } : {}),
    ...(extras.timed ? { timed: extras.timed } : {}),
  };
}

/** A stored set older than this is dropped on load (P0-1: it should not haunt a week-old device). */
export const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface StoredSession {
  savedAt: number;
  /** The account that wrote it: another account's copy is ignored and cleared. */
  userId: string | null;
  data: PracticeSessionData;
}

function readStored(raw: string | null): StoredSession | null {
  if (!raw) return null;
  const stored = JSON.parse(raw) as Partial<StoredSession> | null;
  const data = stored?.data;
  if (
    !stored ||
    typeof stored.savedAt !== "number" ||
    !data ||
    !Array.isArray(data.questions) ||
    typeof data.index !== "number" ||
    typeof data.results !== "object" ||
    data.results === null
  ) {
    return null;
  }
  return { savedAt: stored.savedAt, userId: stored.userId ?? null, data };
}

/**
 * The tab keeps its set in `sessionStorage`; a copy with a timestamp goes to `localStorage`, so a
 * new tab, an app restart on a phone or the next day can offer "Fortsätt din omgång". Both copies
 * carry the user id. Two tabs writing the durable copy: the last write wins, so tab B's set replaces
 * tab A's there, while each tab keeps resuming its own from `sessionStorage`.
 */
export function saveSession(data: PracticeSessionData): void {
  const stored: StoredSession = { savedAt: Date.now(), userId: getStorageOwner(), data };
  const json = JSON.stringify(stored);
  try {
    window.sessionStorage.setItem(STORAGE_KEY, json);
  } catch {
    /* storage full or unavailable (private mode) — the session still works in memory */
  }
  // No known owner (profile not loaded or signed out): the durable copy would be unattributable.
  if (stored.userId === null) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, json);
  } catch {
    /* ignore: the tab copy above still holds */
  }
}

/** The durable copy, or null when absent, unreadable, older than the TTL or another account's (then dropped). */
export function loadStoredSession(now: number = Date.now()): PracticeSessionData | null {
  try {
    const stored = readStored(window.localStorage.getItem(STORAGE_KEY));
    if (!stored) return null;
    if (now - stored.savedAt > SESSION_TTL_MS || !belongsToOwner(stored.userId, true)) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return stored.data;
  } catch {
    return null;
  }
}

export function loadSession(): PracticeSessionData | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const stored = readStored(raw);
    if (!stored && raw) {
      // Written by an older build, as the bare set: a tab's own copy, gone on sign-out.
      const legacy = JSON.parse(raw) as PracticeSessionData | null;
      if (legacy && Array.isArray(legacy.questions)) return legacy;
    }
    if (stored) {
      if (belongsToOwner(stored.userId)) return stored.data;
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* fall through to the durable copy */
  }
  return loadStoredSession();
}

export function clearSession(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** The set to offer "Fortsätt din omgång" for: stored, unfinished and not past its last question. */
export function resumableStoredSession(now: number = Date.now()): PracticeSessionData | null {
  const data = loadSession();
  if (!data || data.index >= data.questions.length || isSessionComplete(data)) return null;
  if (now - data.startedAt > SESSION_TTL_MS) return null;
  return data;
}

export function sessionScore(data: PracticeSessionData): { score: number; maxScore: number } {
  let score = 0;
  let maxScore = 0;
  for (const question of data.questions) {
    const result = data.results[question.id];
    if (!result?.answered) continue;
    score += result.score ?? 0;
    maxScore += result.maxScore ?? 0;
  }
  return { score, maxScore };
}

export function isSessionComplete(data: PracticeSessionData): boolean {
  return data.questions.every((question) => data.results[question.id]?.terminal);
}

/**
 * Mirrors `scoreOpen` (`shared/grading.ts`): the self-assessed score is the sum of the ticked
 * rubric points (each known id once), capped at the question's points. True while that score
 * is below full marks, which makes "what did you miss" required (learning-evidence decision #5).
 */
export function openBelowFullMarks(
  rubric: readonly { id: string; points: number }[],
  ticked: readonly string[],
  points: number,
): boolean {
  const pointsById = new Map(rubric.map((item) => [item.id, item.points]));
  let raw = 0;
  for (const id of new Set(ticked)) raw += pointsById.get(id) ?? 0;
  const score = Math.round(Math.min(raw, points) * 100) / 100;
  return score < points;
}

/**
 * Quick answers to "Vilken princip missade du?" (slice C, P1-9; ruling 3). Any of them satisfies
 * the requirement below full marks; the free text stays optional next to it. The spec's four
 * chips only: "Ge upp" ends a problem without self-grading, so this question never follows it and
 * a "Jag gav upp" chip would mislabel a problem the student did work (review I2).
 */
export const MISSED_CHIPS = ["wrongPrinciple", "wrongSetup", "calcError", "dontKnow"] as const;
export type MissedChip = (typeof MISSED_CHIPS)[number];

export function isMissedChip(value: string | undefined): value is MissedChip {
  return (MISSED_CHIPS as readonly string[]).includes(value ?? "");
}

/**
 * The `missed` text sent with the answer: the chip's label, then the student's own words, or just
 * one of them; "" when neither is given. Stored as the student saw it (the UI is Swedish).
 */
export function composeMissed(chipLabel: string | undefined, text: string): string {
  const own = text.trim();
  if (chipLabel && own) return `${chipLabel} – ${own}`;
  return chipLabel ?? own;
}

/**
 * The session with `question` inserted right after the question whose id is `afterId` (after the
 * follow-ups already inserted from it, so two twins keep the order they were added in), with an
 * empty result (carrying `followUp` when it is a follow-up, step 2.6), or `null` when `afterId` is
 * absent from the session or `question` is already in it (a question appears at most once).
 * Splices by id rather than by `data.index` (bug 1, C18): by the time a fetched related question
 * arrives, `index` may already have advanced past the question it belongs after.
 */
export function insertAfter(
  data: PracticeSessionData,
  afterId: string,
  question: PublicQuestion,
  followUp?: FollowUpState,
): PracticeSessionData | null {
  if (question.id in data.results) return null;
  let at = data.questions.findIndex((q) => q.id === afterId);
  if (at < 0) return null;
  // The follow-ups of `afterId` sit right behind it; the new one goes after the last of them.
  while (data.results[data.questions[at + 1]?.id ?? ""]?.followUp?.of === afterId) at += 1;
  const questions = [...data.questions];
  questions.splice(at + 1, 0, question);
  const result: QuestionResult = followUp ? { ...emptyResult(), followUp } : emptyResult();
  return { ...data, questions, results: { ...data.results, [question.id]: result } };
}

/**
 * Caps `raw` to `max` UTF-16 units, then drops a trailing lone high surrogate, so the result
 * never exceeds the server's `z.string().max(4000)` cap (a UTF-16 length; a code-point cut
 * could exceed it) and never leaves half a surrogate pair.
 */
export function capOpenText(raw: string, max: number = OPEN_TEXT_MAX): string {
  return raw.slice(0, max).replace(/[\uD800-\uDBFF]$/, "");
}
